import {getCheckoutClient} from '../config/CheckoutClient';
import {setTickets, fetchTickets} from './tickets';
import {setProductSales, fetchProducts, updateProductQuantities} from './product-sales';
import {setBundleSales, fetchBundles} from './bundle-sales';
import {setLoading} from './loading';
import {triggerTransition} from '@uirouter/redux';
import { setCheckoutOverlay, addAlert, addServerErrors, refreshCheckoutSteps, goToConfirmation, clearAlerts } from './ui';
import {fetchDeliveryOptions, submitDelivery} from '../views/delivery/actions/delivery';
import { fetchQuestions } from '../views/questions/actions/questions';
import { fetchCouponUsage } from '../views/coupon/actions/coupon';
import { submitCoupon, setAutoCouponAmount } from 'checkout/views/coupon/actions/coupon';
import { setPriceLevels, setProducts, getEventPriceLevels, setBundles } from 'checkout/actions/event';
import { getNumTicketsByLevel } from 'checkout/views/ticket-picker/selectors/tickets';
import { updateTicketQuantities } from 'checkout/actions/tickets';
import { getConfigurationQuantities } from 'checkout/views/product-picker/selectors/products';
import { addBundles } from 'checkout/actions/bundle-sales';
import { submitInsurance } from 'checkout/views/insurance/actions/insurance';
import { getNoDeliveryOptions, getMobileDeliveryOptions, getWillCallDeliveryOptions, getEmailDeliveryOptions } from 'checkout/selectors/delivery';
import { submitMemberInfo } from 'checkout/views/member/actions/member';
import { isKioskMode, getKioskOptions, allStepsComplete } from 'checkout/selectors/ui';
import { removeListingItem, getListingItem, setListingItem } from 'listing/helpers/storage';
import { getErrorText } from 'checkout/helpers/helpers';
import { fetchDonations, updateDonations } from '../views/donations/actions/donations';
import { reservationActive, isReservationFree } from '../selectors/reservation';
import { zeroDollarPayment } from './payment';
import {getAllAvailableDeliveryOptions} from '../selectors/delivery';
import { setFee } from './fees';

export function setReservationData(secret, reservation) {
    return {type: "RESERVATION_DATA_SET", secret, reservation}
}

export function reservationCreated() {
    return {type: "RESERVATION_CREATED"}
}

export function reservationCompleted() {
    return {type: "RESERVATION_COMPLETED"}
}

export function clearReservationData(reset = true) {
    return (dispatch, getState) => {
        let state = getState();

        clearReservationLocalStorage(state.ui.listingSlug);

        // global TicketGuardian class needed to clear session
        if(typeof tg === "function"){
            try {
                tg('clearSession');
            } catch(err) {
                //this can throw an error if the Ticket Guardian widget is not done loading
            }
        }
        dispatch(getClearReservationDataAction(reset));
    }
}

export function clearReservationLocalStorage(slug) {
    removeListingItem(slug, "secret");
    removeListingItem(slug, "token");
}

export function getClearReservationDataAction(reset = true) {
    return {type: "RESERVATION_CLEARED", reset};
}

export function addListingToReservation(listing) {
    return {type: "ADD_LISTING", listing};
}

export function createReservation(slug) {
    return (dispatch, getState) => {
        dispatch(setLoading("RESERVATION_CREATE_REQUEST"));
        dispatch(clearAlerts());
        let state = getState();

        let createArgs = {
            seller_id: state.event.seller_id,
            captureMethod: state.payment.method,
            listingId: state.ui.listingId,
        };

        if(state.reservation.purchaseUser) {
            createArgs.purchaseUser = state.reservation.purchaseUser;
        }

        return getCheckoutClient(state.ui.apiUrl).createResource('reservations', createArgs, '').then((result) => {
            let secret = result.header['reservation-secret'];
            let token = result.body.data.id;
            setListingItem(state.ui.listingSlug, "secret", secret);
            setListingItem(state.ui.listingSlug, "token", token);
            dispatch(reservationCreated());
            dispatch(setReservationData(secret, result.body.data));

            let bulkRequests = [];
            // this calculation could probably be moved into a selector
            let ticketPayload = {tickets: []};
            for (const id in state.ticketPicker.quantities) {
                ticketPayload.tickets.push({event_id: state.priceLevel.levels[id].eventId, pricing_level_id: id, quantity: state.ticketPicker.quantities[id]});
            }

            if(ticketPayload.tickets.length > 0) {
                bulkRequests.push({type: 'tickets', payload: ticketPayload});
            }

            let bundlePayload = {bundles: []};
            for (const id in state.bundlePicker.bundleQuantities) {
                bundlePayload.bundles.push({
                    eventId: state.bundles.bundles[id].eventId,
                    bundleId: id,
                    quantity: state.bundlePicker.bundleQuantities[id],
                    productConfigs: (state.bundlePicker.selectedConfigurations[id]) ? getBundleProductConfig(state.bundlePicker.selectedConfigurations[id]) : []
                });
            }

            if(bundlePayload.bundles.length > 0) {
                bulkRequests.push({type: 'bundlesale', payload: bundlePayload});
            }

            let productPayload = {products: []};
            for (const configId in state.productPicker.selectedConfigurations) {
                productPayload.products.push({
                    event_id: (state.event.childEvent) ? state.event.childEvent.event_id : Object.values(state.event.events)[0].event.event_id,
                    product_configuration_id: configId,
                    quantity: state.productPicker.selectedConfigurations[configId]
                });
            }

            if (productPayload.products.length) {
                bulkRequests.push({type: 'products', payload: productPayload});
            }

            return sendBulkRequest(state.ui.apiUrl, token, secret, bulkRequests).then((res) => {
                if(res.tickets) {
                    processTicketResponse(dispatch, state, res.tickets);
                }

                if(res.bundlesale) {
                    processBundleResponse(dispatch, state, res.bundlesale);
                }

                if(res.products) {
                    processProductResponse(dispatch, state, res.products);
                }

                startTick()(dispatch, getState);
                if (state.coupon.code !== "" && Object.keys(state.coupon.couponData).length === 0 && state.coupon.showSuccess === true) {
                    return submitCoupon(state.coupon.code)(dispatch, getState).then(() => {
                        afterBulk(dispatch, getState, token);
                    }); // submitting a coupon fetches the reservation itself
                } else {
                    // need to fetch a second time because now it has tickets
                    return fetchReservation(token, secret)(dispatch, getState).then(() => {
                        afterBulk(dispatch, getState, token);
                    });
                }
            }).catch(e => {
                let errors = getCreationErrors(e);
                dispatch(addServerErrors(errors));
                return dispatch(deleteReservation(false)).then(() => {
                    dispatch(setLoading("RESERVATION_CREATE_FAILURE"));
                });
            });
        }).catch(e => {
            let errors = getCreationErrors(e);
            dispatch(addServerErrors(errors));
            return dispatch(deleteReservation(false)).then(() => {
               dispatch(setLoading("RESERVATION_CREATE_FAILURE"));
            });
        });
    }
}

function afterBulk(dispatch, getState, token) {
    dispatch(setLoading("RESERVATION_CREATE_SUCCESS"));
    let state = getState();
    //after creating the reservation:
    //if all steps are complete and free reservation, submit free payment and go to confirm
    //else, transition to checkout for next steps
    const postCreateReservationTransition = () => {
        if (allStepsComplete(state) && isReservationFree(state)) {
            zeroDollarPayment()(dispatch, getState).then((result) => {
                dispatch(goToConfirmation());
            }).catch(error => {/*handled in zeroDollarPayment*/});
        } else {
            dispatch(triggerTransition("checkout", {token: token}));
        }
    }

    if(isKioskMode(state)) {
        const kioskOptions = getKioskOptions(state);
        const availableDeliveryOptions = getAllAvailableDeliveryOptions(state);
        const collectingCustomerInfo = kioskOptions.collectCustomerName || kioskOptions.collectCustomerEmail;
        const hasMobileDelivery = kioskOptions.allowMobileDelivery && getMobileDeliveryOptions(state).length;
        const hasWillCallDelivery = kioskOptions.allowWillCallDelivery && getWillCallDeliveryOptions(state).length;
        const hasEmailDelivery = kioskOptions.allowEmailDelivery && getEmailDeliveryOptions(state).length;
        let noDeliveryOptions = getNoDeliveryOptions(state);
        let noDelivery = noDeliveryOptions.length ? noDeliveryOptions[0] : null;
        if (availableDeliveryOptions.length) {
            if(noDelivery || !kioskOptions.printReceiptsAndTickets) {
                //if this kiosk is not configured to allow delivery methods, submit No Delivery method.
                //If it has delivery methods, submit later when the user chooses a method.
                if (!hasMobileDelivery && !hasWillCallDelivery && !hasEmailDelivery) {
                    dispatch(setDeliveryMethod(noDelivery));
                    return dispatch(submitDelivery(noDelivery.id)).then(() => {
                        //if we are collecting any customer info in kiosk mode, we won't need to submit a member request
                        //here because member info will be submitted in the registration checkout step
                        if (!collectingCustomerInfo) {
                            return dispatch(submitMemberInfo()).then(() => {
                                postCreateReservationTransition();
                            });
                        } else {
                            postCreateReservationTransition();
                        }
                    });
                } else {
                    if (!collectingCustomerInfo) {
                        return dispatch(submitMemberInfo()).then(() => {
                            postCreateReservationTransition();
                        });
                    } else {
                        postCreateReservationTransition();
                    }
                }

            } else {
                dispatch(addAlert("Event needs to have a 'No Delivery' method assigned to it."));
                // @todo should probably throw an error cause this is misconfigured
            }
        } else {
            dispatch(addAlert("Event does not have any delivery methods assigned to it."));
        }
    } else {
        postCreateReservationTransition();
    }
}

function getCreationErrors(e) {
    let errors = [];
    if(typeof e === "string") {
        errors = [{detail: e}];
    } else if(Array.isArray(e)) {
        errors = e;
    } else if(e instanceof Error) {
        errors = [{detail: e.message}];
    } else {
        errors = e.response.body.errors;
    }

    for(let i = 0; i < errors.length; i++) {
        if(errors[i].detail === "exceeded_ticket_limit_select_max_to_continue") {
            errors[i].detail = "Selection exceeds the maximum ticket limit."
        }
    }

    return errors;
}

export function sendBulkRequest(apiUrl, token, secret, requests) {
    let req = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({secret, requests})
    };

    return fetch(apiUrl + '/reservations/' + token + '/bulk', req)
        .then((res) => {
            if(res.status === 200) {
                return res.json();
            }
            if(res.status === 500) {
                throw new Error('checkout__reservation_error');
            }
        });
}

function getBundleProductConfig(selectedConfigurations) {
    let configurations = {};
    Object.keys(selectedConfigurations).map(productId => {
        let configurationIds = [];
        Object.keys(selectedConfigurations[productId]).map(keyName => {
            Object.keys(selectedConfigurations[productId][keyName]).map(configId => {
                configurationIds.push(configId);
            })
        });

        configurations[productId] = configurationIds;
    });

    return configurations;
}

function processBundleResponse(dispatch, state, bundles) {
    if(bundles.errors) {
        throw new Error(getErrorText(bundles.errors));
    }

    let listing = {
        event: {...Object.values(state.event.events)[0]},
        selectedListing: {...state.eventSeries.selectedListing},
        venue: {...state.venue},
        priceLevels: {...state.priceLevels}
    };
    dispatch(addListingToReservation(listing));
    dispatch(setBundleSales(bundles));
    return Promise.resolve();
}

function processTicketResponse(dispatch, state, tickets) {
    if(tickets.errors) {
        throw new Error(getErrorText(tickets.errors));
    }

    let listing = {
        event: {...Object.values(state.event.events)[0]},
        selectedListing: {...state.eventSeries.selectedListing},
        venue: {...state.venue},
        priceLevels: {...state.priceLevels}
    };

    dispatch(addListingToReservation(listing));
    dispatch(setTickets(tickets));
}

function processProductResponse(dispatch, state, products) {
        if(products.errors) {
            throw new Error(getErrorText(products.errors));
        }

        let listing = {
            event: {...Object.values(state.event.events)[0]},
            selectedListing: {...state.eventSeries.selectedListing},
            venue: {...state.venue},
            priceLevels: {...state.priceLevels}
        };
        dispatch(addListingToReservation(listing));
        dispatch(setProductSales(products));
}

export function fetchReservation(token, secret, tick = true) {
    return (dispatch, getState) => {
        let state = getState();
        dispatch(setLoading("RESERVATION_FETCH_REQUEST"));
        return getCheckoutClient(state.ui.apiUrl).getResource('reservations', token, '', secret).then((result) => {
            let reservationData = result.body.data;
            if(reservationData.attributes.autoCouponAmount !== null) {
                dispatch(setAutoCouponAmount(reservationData.attributes.autoCouponAmount));
            }

            let requests = [
                fetchDeliveryOptions(token, secret)(dispatch, getState),
                fetchQuestions(token, secret)(dispatch, getState),
                fetchDonations(token, secret)(dispatch, getState),
            ];

            if (state.coupon.code !== "" && state.coupon.showSuccess === true) {
                requests.push(fetchCouponUsage(token, secret)(dispatch, getState));
            }

            if(reservationData.attributes.ticket_count > 0) {
                requests.push(fetchTickets(token, secret)(dispatch, getState));
            }

            if(reservationData.attributes.product_count > 0) {
                requests.push(fetchProducts(token, secret)(dispatch, getState));
            }

            if(reservationData.attributes.bundle_count > 0) {
                requests.push(fetchBundles(token, secret)(dispatch, getState));
            }

            dispatch(setReservationData(secret, reservationData));
            return Promise.all(requests).then(() => {
                if(tick && !reservationData.attributes.isCompleted) {
                    dispatch(startTick());
                }
                // fetch current state after everything's refreshed
                let state = getState();
                const deliveryOptions = getAllAvailableDeliveryOptions(state);
                if(deliveryOptions.length === 1 && !reservationData.attributes.selected_delivery_option) {
                    dispatch(setDeliveryMethod(deliveryOptions[0]));
                }
                if(isKioskMode(state)) {
                    dispatch(refreshCheckoutSteps(state));
                }
                dispatch(setLoading("RESERVATION_FETCH_SUCCESS"));
            });
        }).catch((error) => {
            dispatch(clearReservationData());
            dispatch(setLoading("RESERVATION_FETCH_FAILURE"));
        });
    }
}

export function deleteReservation(reset = true) {
    return (dispatch, getState) => {
        dispatch(setLoading("RESERVATION_DELETE_REQUEST"));
        let state = getState();
        let token = state.reservation.token;
        let secret = state.reservation.secret;

        // reservation is complete, clear our local data but no need to send a delete request
        if(state.reservation.isCompleted || !reservationActive(state)) {
            wipeLocalReservationData(dispatch, getState, reset);
            return Promise.resolve();
        }

        return getCheckoutClient(state.ui.apiUrl).deleteResource('reservations', token, secret).then((result) => {
            wipeLocalReservationData(dispatch, getState, reset);
        }).catch((error) => {
            wipeLocalReservationData(dispatch, getState, reset);
        });
    }
}

function wipeLocalReservationData(dispatch, getState, reset) {
    let state = getState();
    let event = Object.values(state.event.events)[0].event;

    //clearReservation will set this back to false
    const isAccessCodeApplied = state.coupon.isAccessCodeApplied;

    dispatch(clearReservationData(reset));
    if (!event.isOnsale && (event.hasPresale || (state.event.childEvent && state.event.childEvent.event.hasPresale))) {
        dispatch(setPriceLevels({}));
        dispatch(setProducts({}));
        dispatch(setBundles({}));
    }

    if (isAccessCodeApplied === true) {
        dispatch(getEventPriceLevels());
    }

    dispatch(setLoading("RESERVATION_DELETE_SUCCESS"));
    if (!event.isRecurring) dispatch(triggerTransition("ticketPicker", {listing: event.listing}));
    if(reset) {
        dispatch(setCheckoutOverlay(false));
    }
    removeListingItem(state.ui.listingSlug, 'selectedEventId');
}

export function fetchReservationIfNeeded(tick = true) {
    return (dispatch, getState) => {
        let state = getState();
        let token = state.reservation.token;
        let secret = state.reservation.secret;

        // reservation token and secret already in state means nothing to fetch
        if(token !== "" && secret !== "") {
            return Promise.resolve();
        }

        // if not already in state then try to pull from localStorage and rehydrate
        token = getListingItem(state.ui.listingSlug, "token");
        secret = getListingItem(state.ui.listingSlug, "secret");

        // if we're missing either the token or secret (or both) then we can't rehydrate
        if(!token || !secret) {
            return Promise.reject("Unable to rehydrate reservation");
        }

        return fetchReservation(token, secret, tick)(dispatch, getState);
    }
}

export function startTick() {
    return (dispatch, getState) => {
        let state = getState();
        if(!state.reservation.tickInterval) {
            let interval = setInterval(() => {
                let state = getState();
                if(state.reservation.hasPayments) {
                    clearInterval(interval);
                    return;
                }

                if(state.reservation.time_remaining <= 0) {
                    deleteReservation()(dispatch, getState);
                    dispatch(triggerTransition("ticketPicker", {listing: getState().event.listing}));
                    dispatch(setCheckoutOverlay(false));
                }
                dispatch(tick());
            }, 1000);
            dispatch(tickSet(interval));
        }
    }
}

export function updateQuantities() {
    return (dispatch, getState) => {
        dispatch(setLoading("RESERVATION_CREATE_REQUEST"));
        let state = getState();
        let token = state.reservation.token;
        let secret = state.reservation.secret;

        let requests = [];

        // add tickets requests
        let quantities = {tickets: []};
        let ticketPickerQuantities = state.ticketPicker.quantities;
        let selectedQuantities = getNumTicketsByLevel(state);
        for (const levelId in ticketPickerQuantities) {
            let totalQuantity = ticketPickerQuantities[levelId];
            if(selectedQuantities[levelId]) {
                totalQuantity += selectedQuantities[levelId];
            }
            quantities.tickets.push({pricing_level_id: levelId, quantity: totalQuantity, event_id: state.priceLevel.levels[levelId].eventId});
        }
        if (quantities.tickets.length) {
            requests.push(dispatch(updateTicketQuantities(quantities, token, secret, false)));
        }

        // add products requests
        let configQuantities = {products: []};
        let productPickerQuantities = state.productPicker.selectedConfigurations;
        let selectedConfigurationQuantities = getConfigurationQuantities(state);
        if (Object.keys(productPickerQuantities).length > 0) {
            for (const configId in productPickerQuantities) {
                let configTotalQty = productPickerQuantities[configId];
                if (selectedConfigurationQuantities[configId]) {
                    configTotalQty += selectedConfigurationQuantities[configId];
                }
                configQuantities.products.push({product_configuration_id: configId, quantity: configTotalQty, event_id: state.products.configs[configId].eventId});
            }

            if (configQuantities.products.length) {
                requests.push(dispatch(updateProductQuantities(configQuantities, token, secret)));
            }
        }

        // add bundles requests
        let bundlePayload = {bundles: []};
        let bundlePickerQuantities = state.bundlePicker.bundleQuantities;
        let selectedBundleConfigurations = state.bundlePicker.selectedConfigurations;
        let bundles = state.bundles.bundles;
        for (const bundleId in bundlePickerQuantities) {
            bundlePayload.bundles.push({
                eventId: bundles[bundleId].eventId,
                bundleId: bundleId,
                quantity: bundlePickerQuantities[bundleId],
                productConfigs: (selectedBundleConfigurations[bundleId]) ? getBundleProductConfig(selectedBundleConfigurations[bundleId]) : []
            });
        }

        if (bundlePayload.bundles.length > 0) {
            requests.push(dispatch(addBundles(bundlePayload, token, secret)));
        }

        return Promise.all(requests).then(() => {
            let updateRequests = [];
            if (state.coupon.code !== "" && Object.keys(state.coupon.couponData).length === 0 && state.coupon.showSuccess === true) {
                updateRequests.push(dispatch(submitCoupon(state.coupon.code)));
            }

            if (state.donations.hasDonations) {
                updateRequests.push(dispatch(updateDonations()));
            }

            Promise.all(updateRequests).then(() => {
                return Promise.all([
                    dispatch(fetchTickets(token, secret)),
                    dispatch(fetchProducts(token, secret)),
                    dispatch(fetchBundles(token, secret)),
                ]).then(() => {
                    dispatch(fetchReservation(token, secret)).then(() => {
                        dispatch(setLoading("RESERVATION_CREATE_SUCCESS"));
                        dispatch(triggerTransition('checkout', {token: token}));
                    });
                });
            }).catch((e) => {
                dispatch(setLoading("RESERVATION_CREATE_FAILURE"));
            });

        }).catch((e) => {
            dispatch(setLoading("RESERVATION_CREATE_FAILURE"));
        });
    }
}

export function clearActiveReservationDetails() {
    return (dispatch, getState) => {
        dispatch(clearReservationData());
        dispatch(setPriceLevels({}));
        dispatch(setProducts({}));
        dispatch(setBundles({}));
    }
}

export function tick() {
    return {type: "TICK"}
}

export function tickSet(interval) {
    return {type: "TICK_SET", interval}
}

export function stopTick() {
    return {type: "TICK_CLEAR"}
}

export function setDeliveryMethod(selectedDelivery) {
    return (dispatch, getState) => {
        dispatch(_setDeliveryMethod(selectedDelivery));
        dispatch(setFee({type: 'delivery', id: selectedDelivery.id, price: selectedDelivery.price}))
    }
}

export function _setDeliveryMethod(selectedDelivery) {
    return {type: "DELIVERY_METHOD_SET", selectedDelivery};
}

export function setCouponCode(code) {
    return {type: "SET_COUPON_CODE", code};
}

export function setCouponData(couponData) {
    return {type: "SET_COUPON_DATA", couponData};
}

export function setPurchaseUser(id) {
    return {type: "SET_PURCHASE_USER", id};
}
